import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  sportsService,
  organizationsService,
  venuesService,
  teamsService,
  playersService,
  officialsService,
  tournamentsService,
  matchesService,
  registrationsService,
  standingsService,
  bracketsService,
  resultsService,
  statisticsService,
  authService,
  usersService,
} from '@/api/services';
import { api } from '@/api/generated';
import { promptForPushAfterFavorite } from '@/services/pushNotifications';
import { sortSports } from '@/utils/sports';
import type {
  Sport,
  Organization,
  Venue,
  Team,
  Player,
  Official,
  Tournament,
  Registration,
  Match,
  MatchEvent,
  StandingTable,
  Bracket,
  PlayerStats,
  LeaderboardPlayers,
  TournamentResults,
  PaginatedResponse,
  RefreshRequest,
  AuthEmailRequest,
  AuthEmailConfirmRequest,
  CreateTournamentRequest,
  UpdateTournamentRequest,
  TournamentStage,
  TournamentStageStatus,
  TournamentStageType,
  StageSettings,
  CreateMatchRequest,
  CreateMatchEventRequest,
  ReportResultRequest,
  ScheduleGenerationRequest,
  ScheduleSaveRequest,
  SportType,
  SportSettings,
} from '@/types';

// ============ QUERY KEYS ============
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
  },
  home: {
    all: ['home'] as const,
    list: (params?: Record<string, unknown>) => ['home', params] as const,
  },
  me: {
    favorites: ['me', 'favorites'] as const,
    roles: (userId: string) => ['me', 'roles', userId] as const,
  },
  sports: {
    all: ['sports'] as const,
    detail: (id: string) => ['sports', id] as const,
  },
  organizations: {
    all: ['organizations'] as const,
    list: (params?: Record<string, unknown>) => ['organizations', 'list', params] as const,
    detail: (id: string) => ['organizations', id] as const,
    tournaments: (id: string, params?: Record<string, unknown>) => ['organizations', id, 'tournaments', params] as const,
  },
  venues: {
    all: ['venues'] as const,
    list: (params?: Record<string, unknown>) => ['venues', 'list', params] as const,
    detail: (id: string) => ['venues', id] as const,
  },
  teams: {
    all: ['teams'] as const,
    list: (params?: Record<string, unknown>) => ['teams', 'list', params] as const,
    detail: (id: string) => ['teams', id] as const,
  },
  players: {
    all: ['players'] as const,
    list: (params?: Record<string, unknown>) => ['players', 'list', params] as const,
    detail: (id: string) => ['players', id] as const,
    stats: (id: string, params?: Record<string, unknown>) => ['players', id, 'stats', params] as const,
  },
  officials: {
    all: ['officials'] as const,
    list: (params?: Record<string, unknown>) => ['officials', 'list', params] as const,
    detail: (id: string) => ['officials', id] as const,
  },
  tournaments: {
    all: ['tournaments'] as const,
    list: (params?: Record<string, unknown>) => ['tournaments', 'list', params] as const,
    detail: (id: string) => ['tournaments', id] as const,
    settings: (id: string) => ['tournaments', id, 'settings'] as const,
    standings: (id: string) => ['tournaments', id, 'standings'] as const,
    stageStandings: (id: string, stageId?: string) => ['tournaments', id, 'standings', stageId] as const,
    stages: (id: string) => ['tournaments', id, 'stages'] as const,
    bracket: (id: string) => ['tournaments', id, 'bracket'] as const,
    matches: (id: string, params?: Record<string, unknown>) => ['tournaments', id, 'matches', params] as const,
    registrations: (id: string, params?: Record<string, unknown>) => ['tournaments', id, 'registrations', params] as const,
    results: (id: string) => ['tournaments', id, 'results'] as const,
    playerStats: (id: string, params?: Record<string, unknown>) => ['tournaments', id, 'playerStats', params] as const,
    leaderboard: (id: string, params?: Record<string, unknown>) => ['tournaments', id, 'leaderboard', params] as const,
  },
  registrations: {
    all: ['registrations'] as const,
    list: (params?: Record<string, unknown>) => ['registrations', 'list', params] as const,
    detail: (id: string) => ['registrations', id] as const,
  },
  matches: {
    all: ['matches'] as const,
    list: (params?: Record<string, unknown>) => ['matches', 'list', params] as const,
    detail: (id: string) => ['matches', id] as const,
    events: (id: string) => ['matches', id, 'events'] as const,
  },
  standings: {
    byTournament: (id: string) => ['standings', 'tournament', id] as const,
  },
  brackets: {
    byTournament: (id: string) => ['brackets', 'tournament', id] as const,
  },
  results: {
    byTournament: (id: string) => ['results', 'tournament', id] as const,
  },
  statistics: {
    playerStats: (tournamentId: string, params?: Record<string, unknown>) => 
      ['statistics', 'playerStats', tournamentId, params] as const,
    leaderboard: (tournamentId: string, params?: Record<string, unknown>) => 
      ['statistics', 'leaderboard', tournamentId, params] as const,
    playerCareer: (playerId: string, params?: Record<string, unknown>) => 
      ['statistics', 'playerCareer', playerId, params] as const,
  },
};

// ============ AUTH HOOKS ============
export const useEmailStart = () => {
  return useMutation({
    mutationFn: (data: AuthEmailRequest) => authService.emailStart(data),
  });
};

export const useEmailConfirm = () => {
  return useMutation({
    mutationFn: (data: AuthEmailConfirmRequest) => authService.emailConfirm(data),
  });
};

export const useRefreshToken = () => {
  return useMutation({
    mutationFn: (data: RefreshRequest) => authService.refresh(data),
  });
};

export const useLogout = () => {
  return useMutation({
    mutationFn: (refreshToken: string) => authService.logout(refreshToken),
  });
};

// ============ HOME HOOKS ============
export const useHome = (
  params?: Parameters<typeof api.getApiV1Home>[0],
  options?: Omit<UseQueryOptions<import('@/api/generated').GetApiV1HomeResult>, 'queryKey' | 'queryFn'>
) => {
  const normalizedParams = params;

  return useQuery({
    queryKey: queryKeys.home.list(normalizedParams),
    queryFn: () => api.getApiV1Home(normalizedParams as Parameters<typeof api.getApiV1Home>[0]),
    ...options,
  });
};

// ============ ME FAVORITES HOOKS ============
export const useMeFavorites = (
  options?: Omit<UseQueryOptions<{ tournaments?: unknown[]; matches?: unknown[] }, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.me.favorites,
    queryFn: async () => {
      const res = await api.getApiV1MeFavorites();
      return res ?? { tournaments: [], matches: [] };
    },
    ...options,
  });
};

export const useAddTournamentFavorite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tournamentId: string) => api.postApiV1MeFavoritesTournamentsTournamentId(tournamentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.me.favorites });
      qc.invalidateQueries({ queryKey: queryKeys.home.all });
      void promptForPushAfterFavorite().catch(() => undefined);
    },
  });
};

export const useRemoveTournamentFavorite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tournamentId: string) => api.deleteApiV1MeFavoritesTournamentsTournamentId(tournamentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.me.favorites });
      qc.invalidateQueries({ queryKey: queryKeys.home.all });
    },
  });
};

export const useAddMatchFavorite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => api.postApiV1MeFavoritesMatchesMatchId(matchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.me.favorites });
      qc.invalidateQueries({ queryKey: queryKeys.home.all });
      void promptForPushAfterFavorite().catch(() => undefined);
    },
  });
};

export const useRemoveMatchFavorite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => api.deleteApiV1MeFavoritesMatchesMatchId(matchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.me.favorites });
      qc.invalidateQueries({ queryKey: queryKeys.home.all });
    },
  });
};

function getAuthUserId(): string | null {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as { uid?: string };
    return payload.uid || null;
  } catch {
    return null;
  }
}

export const useMyRoles = (enabled: boolean) => {
  const userId = getAuthUserId();
  return useQuery({
    queryKey: queryKeys.me.roles(userId || ''),
    queryFn: () => usersService.getRoles(userId!),
    enabled: enabled && Boolean(userId),
  });
};

// ============ SPORTS HOOKS ============
export const useSports = (options?: UseQueryOptions<Sport[]>) => {
  return useQuery({
    queryKey: queryKeys.sports.all,
    queryFn: () => sportsService.getAll(),
    select: (data) => sortSports(data),
    ...options,
  });
};

export const useSport = (id: string, options?: UseQueryOptions<Sport>) => {
  return useQuery({
    queryKey: queryKeys.sports.detail(id),
    queryFn: () => sportsService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateSport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof sportsService.create>[0]) => sportsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sports.all });
    },
  });
};

export const useUpdateSport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof sportsService.update>[1] }) =>
      sportsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sports.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.sports.detail(id) });
    },
  });
};

export const useDeleteSport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sportsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sports.all });
    },
  });
};

export const useRestoreSport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sportsService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sports.all });
    },
  });
};

// ============ ORGANIZATIONS HOOKS ============
export const useOrganizations = (params?: { page?: number; limit?: number; active?: boolean; q?: string }, options?: UseQueryOptions<PaginatedResponse<Organization>>) => {
  return useQuery({
    queryKey: queryKeys.organizations.list(params),
    queryFn: () => organizationsService.getAll(params),
    ...options,
  });
};

export const useOrganization = (id: string, options?: UseQueryOptions<Organization>) => {
  return useQuery({
    queryKey: queryKeys.organizations.detail(id),
    queryFn: () => organizationsService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof organizationsService.create>[0]) => organizationsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof organizationsService.update>[1] }) =>
      organizationsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.detail(id) });
    },
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
};

export const useRestoreOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationsService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
};

export const useOrganizationTournaments = (
  organizationId: string,
  params?: { page?: number; limit?: number; season?: string | null; sportType?: SportType; status?: string | null },
  options?: UseQueryOptions<PaginatedResponse<Tournament>>
) => {
  return useQuery({
    queryKey: queryKeys.organizations.tournaments(organizationId, params),
    queryFn: () => organizationsService.getTournaments(organizationId, params),
    enabled: !!organizationId,
    ...options,
  });
};

// ============ VENUES HOOKS ============
export const useVenues = (params?: { page?: number; limit?: number; active?: boolean; q?: string }, options?: UseQueryOptions<PaginatedResponse<Venue>>) => {
  return useQuery({
    queryKey: queryKeys.venues.list(params),
    queryFn: () => venuesService.getAll(params),
    ...options,
  });
};

export const useVenue = (id: string, options?: UseQueryOptions<Venue>) => {
  return useQuery({
    queryKey: queryKeys.venues.detail(id),
    queryFn: () => venuesService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateVenue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof venuesService.create>[0]) => venuesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.venues.all });
    },
  });
};

export const useUpdateVenue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof venuesService.update>[1] }) =>
      venuesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.venues.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.venues.detail(id) });
    },
  });
};

export const useDeleteVenue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => venuesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.venues.all });
    },
  });
};

export const useRestoreVenue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => venuesService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.venues.all });
    },
  });
};

// ============ TEAMS HOOKS ============
export const useTeams = (params?: { page?: number; limit?: number; active?: boolean; organizationId?: string; sportType?: SportType; q?: string }, options?: UseQueryOptions<PaginatedResponse<Team>>) => {
  return useQuery({
    queryKey: queryKeys.teams.list(params),
    queryFn: () => teamsService.getAll(params),
    ...options,
  });
};

export const useTeam = (
  id: string,
  options?: Omit<UseQueryOptions<Team>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.teams.detail(id),
    queryFn: () => teamsService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof teamsService.create>[0]) => teamsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof teamsService.update>[1] }) =>
      teamsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(id) });
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
  });
};

export const useRestoreTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamsService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
  });
};

// ============ PLAYERS HOOKS ============
export const usePlayers = (params?: { page?: number; limit?: number; active?: boolean; q?: string }, options?: UseQueryOptions<PaginatedResponse<Player>>) => {
  return useQuery({
    queryKey: queryKeys.players.list(params),
    queryFn: () => playersService.getAll(params),
    ...options,
  });
};

export const usePlayer = (id: string, options?: UseQueryOptions<Player>) => {
  return useQuery({
    queryKey: queryKeys.players.detail(id),
    queryFn: () => playersService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreatePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof playersService.create>[0]) => playersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.players.all });
    },
  });
};

export const useUpdatePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof playersService.update>[1] }) =>
      playersService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.players.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.players.detail(id) });
    },
  });
};

export const useDeletePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => playersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.players.all });
    },
  });
};

export const useRestorePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => playersService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.players.all });
    },
  });
};

export const usePlayerStats = (id: string, params?: { scope?: 'career' | 'season' | 'tournament'; season?: string; tournamentId?: string }, options?: UseQueryOptions<PlayerStats>) => {
  return useQuery({
    queryKey: queryKeys.players.stats(id, params),
    queryFn: () => playersService.getStats(id, params),
    enabled: !!id,
    ...options,
  });
};

// ============ OFFICIALS HOOKS ============
export const useOfficials = (params?: { page?: number; limit?: number; active?: boolean; q?: string }, options?: UseQueryOptions<PaginatedResponse<Official>>) => {
  return useQuery({
    queryKey: queryKeys.officials.list(params),
    queryFn: () => officialsService.getAll(params),
    ...options,
  });
};

export const useOfficial = (id: string, options?: UseQueryOptions<Official>) => {
  return useQuery({
    queryKey: queryKeys.officials.detail(id),
    queryFn: () => officialsService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateOfficial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof officialsService.create>[0]) => officialsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.officials.all });
    },
  });
};

export const useUpdateOfficial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof officialsService.update>[1] }) =>
      officialsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.officials.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.officials.detail(id) });
    },
  });
};

export const useDeleteOfficial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.officials.all });
    },
  });
};

export const useRestoreOfficial = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialsService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.officials.all });
    },
  });
};

// ============ TOURNAMENTS HOOKS ============
export const useTournaments = (params?: { page?: number; limit?: number; organizationId?: string; season?: string | null; sportType?: SportType; status?: string | null; q?: string; mine?: boolean }, options?: UseQueryOptions<PaginatedResponse<Tournament>>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.list(params),
    queryFn: () => tournamentsService.getAll(params),
    ...options,
  });
};

export const useTournament = (id: string, options?: Omit<UseQueryOptions<Tournament>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.detail(id),
    queryFn: () => tournamentsService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTournamentRequest) => tournamentsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.all });
    },
  });
};

export const useUpdateTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTournamentRequest }) =>
      tournamentsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

export const useTournamentSettings = (id: string, options?: Omit<UseQueryOptions<SportSettings>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.settings(id),
    queryFn: () => tournamentsService.getSettings(id),
    enabled: !!id,
    ...options,
  });
};

export const useUpdateTournamentSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SportSettings }) =>
      tournamentsService.updateSettings(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.settings(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

export const useDeleteTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tournamentsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.all });
    },
  });
};

export const useRestoreTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tournamentsService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.all });
    },
  });
};

// ============ TOURNAMENT LIFECYCLE HOOKS ============
export const usePublishTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tournamentsService.publish(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

export const useStartTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tournamentsService.start(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

export const useCompleteTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tournamentsService.complete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.standings(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.matches(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.results(id) });
    },
  });
};

export const useCancelTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tournamentsService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

// ============ TOURNAMENT REGISTRATIONS HOOKS ============
export const useTournamentRegistrations = (id: string, params?: { page?: number; limit?: number; status?: string; participantType?: string }, options?: UseQueryOptions<PaginatedResponse<Registration>>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.registrations(id, params),
    queryFn: () => tournamentsService.getRegistrations(id, params),
    enabled: !!id,
    ...options,
  });
};

export const useRegisterParticipant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tournamentId, data }: { tournamentId: string; data: Parameters<typeof tournamentsService.register>[1] }) =>
      tournamentsService.register(tournamentId, data),
    onSuccess: (_, { tournamentId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.registrations(tournamentId) });
    },
  });
};

// ============ TOURNAMENT SEEDING & SCHEDULING HOOKS ============
export const useSeedTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: Record<string, unknown> }) => tournamentsService.seed(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

export const useGenerateStageSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId, data }: { id: string; stageId: string; data?: ScheduleGenerationRequest }) =>
      tournamentsService.generateStageSchedule(id, stageId, data),
    onSuccess: (_, { id, stageId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.matches(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.stageStandings(id, stageId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.stages(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

export const usePreviewStageSchedule = () => {
  return useMutation({
    mutationFn: ({ id, stageId, data }: { id: string; stageId: string; data?: ScheduleGenerationRequest }) =>
      tournamentsService.previewStageSchedule(id, stageId, data),
  });
};

export const useSaveStageSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId, data }: { id: string; stageId: string; data: ScheduleSaveRequest }) =>
      tournamentsService.saveStageSchedule(id, stageId, data),
    onSuccess: (_, { id, stageId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.matches(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.stageStandings(id, stageId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.stages(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(id) });
    },
  });
};

// ============ TOURNAMENT DATA HOOKS ============
export const useTournamentStandings = (id: string, options?: UseQueryOptions<StandingTable>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.standings(id),
    queryFn: () => tournamentsService.getStandings(id),
    enabled: !!id,
    ...options,
  });
};

export const useTournamentStageStandings = (
  id: string,
  stageId?: string,
  options?: UseQueryOptions<StandingTable>
) => {
  return useQuery({
    queryKey: queryKeys.tournaments.stageStandings(id, stageId),
    queryFn: () => tournamentsService.getStandings(id, stageId ? { stageId } : undefined),
    enabled: !!id,
    ...options,
  });
};

export const useTournamentStages = (id: string, options?: UseQueryOptions<TournamentStage[]>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.stages(id),
    queryFn: () => tournamentsService.getStages(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateTournamentStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tournamentId,
      data,
    }: {
      tournamentId: string;
      data: {
        name: string;
        orderIndex: number;
        stageType: TournamentStageType;
        status?: TournamentStageStatus;
        active?: boolean;
        settings?: StageSettings;
      };
    }) => tournamentsService.createStage(tournamentId, data),
    onSuccess: (_, { tournamentId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.stages(tournamentId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(tournamentId) });
    },
  });
};

export const useUpdateTournamentStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tournamentId,
      stageId,
      data,
    }: {
      tournamentId: string;
      stageId: string;
      data: Partial<{
        name: string;
        orderIndex: number;
        stageType: TournamentStageType;
        status: TournamentStageStatus;
        active: boolean;
        settings: StageSettings;
      }>;
    }) => tournamentsService.updateStage(tournamentId, stageId, data),
    onSuccess: (_, { tournamentId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.stages(tournamentId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.detail(tournamentId) });
    },
  });
};

export const useTournamentBracket = (id: string, options?: UseQueryOptions<Bracket>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.bracket(id),
    queryFn: () => tournamentsService.getBracket(id),
    enabled: !!id,
    ...options,
  });
};

export const useTournamentMatches = (id: string, params?: { page?: number; limit?: number }, options?: UseQueryOptions<PaginatedResponse<Match>>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.matches(id, params),
    queryFn: () => tournamentsService.getMatches(id, params),
    enabled: !!id,
    ...options,
  });
};

export const useCreateTournamentMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tournamentId, data }: { tournamentId: string; data: CreateMatchRequest }) =>
      tournamentsService.createMatch(tournamentId, data),
    onSuccess: (_, { tournamentId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.matches(tournamentId) });
    },
  });
};

// ============ TOURNAMENT RESULTS & STATS HOOKS ============
export const useTournamentResults = (
  id: string,
  options?: Omit<UseQueryOptions<TournamentResults>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.tournaments.results(id),
    queryFn: () => tournamentsService.getResults(id),
    enabled: !!id,
    ...options,
  });
};

export const useTournamentPlayerStats = (id: string, params?: Record<string, unknown>, options?: UseQueryOptions<PaginatedResponse<PlayerStats>>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.playerStats(id, params),
    queryFn: () => tournamentsService.getPlayerStats(id, params),
    enabled: !!id,
    ...options,
  });
};

export const useTournamentLeaderboard = (id: string, params: { metric: string; limit?: number }, options?: Omit<UseQueryOptions<LeaderboardPlayers>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: queryKeys.tournaments.leaderboard(id, params),
    queryFn: () => tournamentsService.getLeaderboard(id, params),
    enabled: !!id && !!params.metric,
    ...options,
  });
};

// ============ REGISTRATIONS HOOKS ============
export const useRegistrations = (params?: { page?: number; limit?: number; tournamentId?: string; status?: 'pending' | 'approved' | 'rejected'; participantType?: 'team' | 'player' }, options?: UseQueryOptions<PaginatedResponse<Registration>>) => {
  return useQuery({
    queryKey: queryKeys.registrations.list(params),
    queryFn: () => registrationsService.getAll(params as Parameters<typeof registrationsService.getAll>[0]),
    ...options,
  });
};

export const useRegistration = (id: string, options?: UseQueryOptions<Registration>) => {
  return useQuery({
    queryKey: queryKeys.registrations.detail(id),
    queryFn: () => registrationsService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useUpdateRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof registrationsService.update>[1] }) =>
      registrationsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail(id) });
    },
  });
};

export const useDeleteRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => registrationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.all });
    },
  });
};

export const useRestoreRegistration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => registrationsService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.all });
    },
  });
};

// ============ MATCHES HOOKS ============
export const useMatches = (params?: { tournamentId?: string; status?: 'scheduled' | 'live' | 'finished' | 'canceled' | 'walkover'; page?: number; limit?: number }, options?: UseQueryOptions<PaginatedResponse<Match>>) => {
  return useQuery({
    queryKey: queryKeys.matches.list(params),
    queryFn: () => matchesService.getAll(params as Parameters<typeof matchesService.getAll>[0]),
    ...options,
  });
};

export const useMatch = (id: string, options?: UseQueryOptions<Match>) => {
  return useQuery({
    queryKey: queryKeys.matches.detail(id),
    queryFn: () => matchesService.getById(id),
    enabled: !!id,
    ...options,
  });
};

export const useUpdateMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateMatchRequest> }) =>
      matchesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.detail(id) });
    },
  });
};

export const useDeleteMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => matchesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.all });
    },
  });
};

export const useRestoreMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => matchesService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.all });
    },
  });
};

// ============ MATCH EVENTS HOOKS ============
export const useMatchEvents = (id: string, options?: UseQueryOptions<MatchEvent[]>) => {
  return useQuery({
    queryKey: queryKeys.matches.events(id),
    queryFn: () => matchesService.getEvents(id),
    enabled: !!id,
    ...options,
  });
};

export const useAddMatchEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, data }: { matchId: string; data: CreateMatchEventRequest }) =>
      matchesService.addEvent(matchId, data),
    onSuccess: (_, { matchId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.events(matchId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.detail(matchId) });
    },
  });
};

export const useUpdateMatchEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, eventId, data }: { matchId: string; eventId: string; data: Partial<CreateMatchEventRequest> }) =>
      matchesService.updateEvent(matchId, eventId, data),
    onSuccess: (_, { matchId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.events(matchId) });
    },
  });
};

export const useDeleteMatchEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, eventId }: { matchId: string; eventId: string }) =>
      matchesService.deleteEvent(matchId, eventId),
    onSuccess: (_, { matchId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.events(matchId) });
    },
  });
};

// ============ MATCH RESULTS HOOKS ============
export const useReportMatchResult = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReportResultRequest }) =>
      matchesService.reportResult(id, data),
    onSuccess: (match, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.all });
      if (match.tournamentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.matches(match.tournamentId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.standings(match.tournamentId) });
      }
    },
  });
};

export const useConfirmMatchResult = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => matchesService.confirmResult(id),
    onSuccess: (match, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.matches.all });
      if (match.tournamentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.matches(match.tournamentId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.standings(match.tournamentId) });
      }
    },
  });
};

// ============ STANDINGS HOOKS ============
export const useStandings = (tournamentId: string, options?: UseQueryOptions<StandingTable>) => {
  return useQuery({
    queryKey: queryKeys.standings.byTournament(tournamentId),
    queryFn: () => standingsService.getByTournament(tournamentId),
    enabled: !!tournamentId,
    ...options,
  });
};

// ============ BRACKETS HOOKS ============
export const useBracket = (tournamentId: string, options?: UseQueryOptions<Bracket>) => {
  return useQuery({
    queryKey: queryKeys.brackets.byTournament(tournamentId),
    queryFn: () => bracketsService.getByTournament(tournamentId),
    enabled: !!tournamentId,
    ...options,
  });
};

// ============ RESULTS HOOKS ============
export const useResults = (tournamentId: string, options?: UseQueryOptions<TournamentResults>) => {
  return useQuery({
    queryKey: queryKeys.results.byTournament(tournamentId),
    queryFn: () => resultsService.getByTournament(tournamentId),
    enabled: !!tournamentId,
    ...options,
  });
};

// ============ STATISTICS HOOKS ============
export const useStatisticsPlayerStats = (tournamentId: string, params?: Record<string, unknown>, options?: UseQueryOptions<PaginatedResponse<PlayerStats>>) => {
  return useQuery({
    queryKey: queryKeys.statistics.playerStats(tournamentId, params),
    queryFn: () => statisticsService.getPlayerStats(tournamentId, params),
    enabled: !!tournamentId,
    ...options,
  });
};

export const useStatisticsLeaderboard = (tournamentId: string, params: { metric: string; limit?: number }, options?: UseQueryOptions<LeaderboardPlayers>) => {
  return useQuery({
    queryKey: queryKeys.statistics.leaderboard(tournamentId, params),
    queryFn: () => statisticsService.getLeaderboard(tournamentId, params),
    enabled: !!tournamentId && !!params.metric,
    ...options,
  });
};

export const useStatisticsPlayerCareer = (playerId: string, params?: Record<string, unknown>, options?: UseQueryOptions<PlayerStats>) => {
  return useQuery({
    queryKey: queryKeys.statistics.playerCareer(playerId, params),
    queryFn: () => statisticsService.getPlayerCareerStats(playerId, params),
    enabled: !!playerId,
    ...options,
  });
};

// ============ SPECIALIZED HOOKS ============

// Live matches hook (with polling)
export const useLiveMatches = (options?: UseQueryOptions<PaginatedResponse<Match>>) => {
  return useQuery({
    queryKey: queryKeys.matches.list({ status: 'live' }),
    queryFn: () => matchesService.getAll({ status: 'live' }),
    refetchInterval: 30000, // Refetch every 30 seconds
    ...options,
  });
};

// Scheduled matches hook
export const useScheduledMatches = (params?: { page?: number; limit?: number }, options?: UseQueryOptions<PaginatedResponse<Match>>) => {
  return useQuery({
    queryKey: queryKeys.matches.list({ status: 'scheduled', ...params }),
    queryFn: () => matchesService.getAll({ status: 'scheduled', ...params }),
    ...options,
  });
};

// Finished matches hook
export const useFinishedMatches = (params?: { page?: number; limit?: number }, options?: UseQueryOptions<PaginatedResponse<Match>>) => {
  return useQuery({
    queryKey: queryKeys.matches.list({ status: 'finished', ...params }),
    queryFn: () => matchesService.getAll({ status: 'finished', ...params }),
    ...options,
  });
};
